import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWithdrawalsMutations } from '../../hooks/useWithdrawalsMutations';
import type { Withdrawal } from '../../models';

export interface CancelWithdrawalModalRef {
  open: (withdrawal: Withdrawal) => void;
  close: () => void;
}

interface CancelWithdrawalModalProps {
  onSuccess?: () => void;
}

export const CancelWithdrawalModal = forwardRef<CancelWithdrawalModalRef, CancelWithdrawalModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('withdrawalsModule');
    const { toast } = useToast();
    const currency = useCurrency();
    const modalRef = useRef<ModalCenterRef>(null);
    const { cancelWithdrawal } = useWithdrawalsMutations();

    const [withdrawal, setWithdrawal] = useState<Withdrawal | null>(null);

    useImperativeHandle(ref, () => ({
      open: (w) => {
        setWithdrawal(w);
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const handleConfirm = async () => {
      if (!withdrawal) return;
      try {
        await cancelWithdrawal.mutateAsync(withdrawal.id);
        toast({
          title: t('modals.cancel.success.title'),
          description: t('modals.cancel.success.description'),
        });
        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.cancelFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isLoading = cancelWithdrawal.isPending;

    return (
      <ModalCenter ref={modalRef} title={t('modals.cancel.title')} width="default">
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t('modals.cancel.description')}</p>

          {withdrawal && (
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <p className="text-sm">
                <span className="font-medium">ID:</span> #{withdrawal.id}
              </p>
              <p className="text-sm">
                <span className="font-medium">Importe:</span> {withdrawal.requested_amount} {currency}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? t('buttons.cancelling') : t('actions.cancel')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CancelWithdrawalModal.displayName = 'CancelWithdrawalModal';
